import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Snackbar,
  Alert,
  Menu,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
} from '@mui/material';
import {
  PersonAdd,
  MoreVert,
  Edit,
  Delete,
  Lock,
  AdminPanelSettings,
  ManageAccounts,
  People,
  Visibility,
  Block,
  CheckCircle,
  Security,
} from '@mui/icons-material';

const usersData = [
  {
    id: 1,
    name: 'John Admin',
    email: 'admin@hotelops.com',
    role: 'Admin',
    status: 'Active',
    lastLogin: '2025-11-16 09:30 AM',
    permissions: ['All'],
  },
  {
    id: 2,
    name: 'Sarah Manager',
    email: 'manager@hotelops.com',
    role: 'Manager',
    status: 'Active',
    lastLogin: '2025-11-16 08:15 AM',
    permissions: ['Bookings', 'Rooms', 'Customers', 'Staff', 'Services', 'Reports'],
  },
  {
    id: 3,
    name: 'Mike Staff',
    email: 'staff@hotelops.com',
    role: 'Staff',
    status: 'Active',
    lastLogin: '2025-11-15 10:00 PM',
    permissions: ['Housekeeping', 'Services'],
  },
  {
    id: 4,
    name: 'Lisa Receptionist',
    email: 'lisa.r@hotelops.com',
    role: 'Staff',
    status: 'Active',
    lastLogin: '2025-11-16 07:00 AM',
    permissions: ['Bookings', 'Customers'],
  },
];

export default function UserManagement() {
  const [users, setUsers] = useState(usersData);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    role: 'Staff',
    password: '',
    status: 'Active',
    permissions: [],
  });

  const allPermissions = [
    'Dashboard',
    'Bookings',
    'Rooms',
    'Customers',
    'Staff',
    'Services',
    'Housekeeping',
    'Inventory',
    'Reports',
    'Settings',
  ];

  const handleMenuClick = (event, user) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleAddUser = () => {
    setSelectedUser(null);
    setUserForm({
      name: '',
      email: '',
      role: 'Staff',
      password: '',
      status: 'Active',
      permissions: [],
    });
    setUserDialogOpen(true);
  };

  const handleEditUser = () => {
    setUserForm({
      name: selectedUser.name,
      email: selectedUser.email,
      role: selectedUser.role,
      password: '',
      status: selectedUser.status,
      permissions: selectedUser.permissions,
    });
    setUserDialogOpen(true);
    setAnchorEl(null);
  };

  const handleSaveUser = () => {
    if (!userForm.name || !userForm.email || (!selectedUser && !userForm.password)) {
      setSnackbar({
        open: true,
        message: 'Please fill in all required fields',
        severity: 'error',
      });
      return;
    }

    if (selectedUser) {
      // Edit existing user
      setUsers(users.map(u => 
        u.id === selectedUser.id 
          ? { ...u, ...userForm, lastLogin: u.lastLogin }
          : u
      ));
      setSnackbar({
        open: true,
        message: 'User updated successfully!',
        severity: 'success',
      });
    } else {
      // Add new user
      const newUser = {
        id: users.length + 1,
        ...userForm,
        lastLogin: 'Never',
      };
      setUsers([...users, newUser]);
      setSnackbar({
        open: true,
        message: 'User created successfully!',
        severity: 'success',
      });
    }
    setUserDialogOpen(false);
  };

  const handleDeleteUser = () => {
    setUsers(users.filter(u => u.id !== selectedUser.id));
    setSnackbar({
      open: true,
      message: 'User deleted successfully',
      severity: 'success',
    });
    setAnchorEl(null);
  };

  const handleViewUser = () => {
    setViewDialogOpen(true);
    setAnchorEl(null);
  };

  const handleToggleStatus = () => {
    const newStatus = selectedUser.status === 'Active' ? 'Inactive' : 'Active';
    setUsers(users.map(u => 
      u.id === selectedUser.id 
        ? { ...u, status: newStatus }
        : u
    ));
    setSnackbar({
      open: true,
      message: `User ${newStatus === 'Active' ? 'activated' : 'deactivated'} successfully`,
      severity: 'success',
    });
    setAnchorEl(null);
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'Admin': return <AdminPanelSettings />;
      case 'Manager': return <ManageAccounts />;
      default: return <People />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'Admin': return '#d32f2f';
      case 'Manager': return '#1976d2';
      default: return '#2e7d32';
    }
  };

  const stats = [
    { label: 'Total Users', value: users.length, color: '#1976d2', icon: People },
    { label: 'Active', value: users.filter(u => u.status === 'Active').length, color: '#2e7d32', icon: CheckCircle },
    { label: 'Admins', value: users.filter(u => u.role === 'Admin').length, color: '#d32f2f', icon: AdminPanelSettings },
    { label: 'Managers', value: users.filter(u => u.role === 'Manager').length, color: '#ed6c02', icon: ManageAccounts },
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            User Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage admin users, roles, and permissions
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<PersonAdd />} onClick={handleAddUser}>
          Add User
        </Button>
      </Box>

      <Grid container spacing={3} mb={3}>
        {stats.map((stat) => {
          const IconComponent = stat.icon;
          return (
            <Grid item xs={6} md={3} key={stat.label}>
              <Card sx={{ bgcolor: `${stat.color}10`, borderLeft: `4px solid ${stat.color}` }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {stat.label}
                      </Typography>
                      <Typography variant="h4" fontWeight="bold" color={stat.color}>
                        {stat.value}
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: stat.color, width: 48, height: 48 }}>
                      <IconComponent />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell><strong>User</strong></TableCell>
              <TableCell><strong>Email</strong></TableCell>
              <TableCell><strong>Role</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Last Login</strong></TableCell>
              <TableCell><strong>Permissions</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id} hover>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar sx={{ bgcolor: getRoleColor(user.role) }}>
                      {getRoleIcon(user.role)}
                    </Avatar>
                    <Typography variant="body2" fontWeight="bold">
                      {user.name}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Chip
                    label={user.role}
                    size="small"
                    sx={{ bgcolor: `${getRoleColor(user.role)}20`, color: getRoleColor(user.role) }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={user.status}
                    size="small"
                    color={user.status === 'Active' ? 'success' : 'default'}
                    icon={user.status === 'Active' ? <CheckCircle /> : <Block />}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{user.lastLogin}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {user.permissions.length === 1 && user.permissions[0] === 'All' 
                      ? 'All Permissions' 
                      : `${user.permissions.length} permissions`}
                  </Typography>
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={(e) => handleMenuClick(e, user)}>
                    <MoreVert />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={handleViewUser}>
          <Visibility fontSize="small" sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={handleEditUser}>
          <Edit fontSize="small" sx={{ mr: 1 }} />
          Edit User
        </MenuItem>
        <MenuItem onClick={handleToggleStatus}>
          {selectedUser?.status === 'Active' ? <Block fontSize="small" sx={{ mr: 1 }} /> : <CheckCircle fontSize="small" sx={{ mr: 1 }} />}
          {selectedUser?.status === 'Active' ? 'Deactivate' : 'Activate'}
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleDeleteUser} sx={{ color: 'error.main' }}>
          <Delete fontSize="small" sx={{ mr: 1 }} />
          Delete User
        </MenuItem>
      </Menu>

      {/* Add/Edit User Dialog */}
      <Dialog open={userDialogOpen} onClose={() => setUserDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <PersonAdd color="primary" />
            <Typography variant="h6">{selectedUser ? 'Edit User' : 'Add New User'}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 2 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Full Name"
                value={userForm.name}
                onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Role"
                value={userForm.role}
                onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
              >
                <MenuItem value="Admin">Admin</MenuItem>
                <MenuItem value="Manager">Manager</MenuItem>
                <MenuItem value="Staff">Staff</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Status"
                value={userForm.status}
                onChange={(e) => setUserForm({ ...userForm, status: e.target.value })}
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={selectedUser ? 'New Password (leave blank to keep current)' : 'Password'}
                type="password"
                value={userForm.password}
                onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                required={!selectedUser}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider />
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom mt={2}>
                <Security sx={{ verticalAlign: 'middle', mr: 1 }} />
                Permissions
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Select which sections this user can access
              </Typography>
              
              {userForm.role === 'Admin' ? (
                <Chip label="Full Access - All Permissions" color="error" />
              ) : (
                <Grid container spacing={1}>
                  {allPermissions.map((permission) => (
                    <Grid item xs={6} md={4} key={permission}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={userForm.permissions.includes(permission)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setUserForm({ 
                                  ...userForm, 
                                  permissions: [...userForm.permissions, permission] 
                                });
                              } else {
                                setUserForm({ 
                                  ...userForm, 
                                  permissions: userForm.permissions.filter(p => p !== permission) 
                                });
                              }
                            }}
                          />
                        }
                        label={permission}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveUser}>
            {selectedUser ? 'Update User' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View User Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ bgcolor: getRoleColor(selectedUser?.role), width: 56, height: 56 }}>
              {selectedUser && getRoleIcon(selectedUser.role)}
            </Avatar>
            <Typography variant="h6">User Details</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Name</Typography>
                <Typography variant="body1" fontWeight="bold">{selectedUser.name}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Email</Typography>
                <Typography variant="body1">{selectedUser.email}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Role</Typography>
                <Chip
                  label={selectedUser.role}
                  size="small"
                  sx={{ bgcolor: `${getRoleColor(selectedUser.role)}20`, color: getRoleColor(selectedUser.role) }}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Status</Typography>
                <Chip
                  label={selectedUser.status}
                  size="small"
                  color={selectedUser.status === 'Active' ? 'success' : 'default'}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">Last Login</Typography>
                <Typography variant="body1">{selectedUser.lastLogin}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider />
                <Typography variant="subtitle1" fontWeight="bold" mt={2} mb={1}>
                  Permissions
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {selectedUser.permissions.map((permission) => (
                    <Chip key={permission} label={permission} size="small" />
                  ))}
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          <Button variant="contained" startIcon={<Edit />} onClick={() => { setViewDialogOpen(false); handleEditUser(); }}>
            Edit User
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
